import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button } from '../components/common';
import { post, API_URL } from '../utils/api';

// Validation schema
const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

function ForgotPassword() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    
    try {
      // console.log('Requesting password reset for:', values.email);
      
      await post(`${API_URL}/auth/forgot-password`, {
        email: values.email
      });
      
      setSubmittedEmail(values.email);
      setIsSubmitted(true);
    } catch (err) {
      // console.error('Password reset request error:', err);
      
      // Check if this is a 400 error with a specific message about no account found
      if (err.response?.status === 400 && err.response?.data?.error) {
        // Display the specific error message from the API
        setError(err.response.data.error);
      } else if (err.response?.status >= 500) {
        setError('An error occurred while processing your request. Please try again later.');
      } else {
        // For other client errors, show a generic message
        setError('Unable to process password reset request. Please check your email and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-md mx-auto mt-8 mb-12">
        <h1 className="text-3xl font-bold text-center mb-4 text-hooray-red font-mont">
          Check Your Email
        </h1>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            {/* Email icon */}
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 3.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            
            <p className="text-gray-700 mb-4">
              If an account with the email <strong>{submittedEmail}</strong> exists, 
              you will receive a password reset link shortly.
            </p>
            
            <p className="text-sm text-gray-600 mb-6">
              The link will expire in 24 hours for security purposes.
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              
              <Button
                onClick={() => {
                  setIsSubmitted(false);
                  setSubmittedEmail('');
                }}
                variant="outline"
                className="w-full mb-3"
              >
                Try Again
              </Button>
            </div>
            
            <div className="text-center">
              <Link 
                to="/login" 
                className="text-sm text-hooray-red hover:text-hooray-yellow font-medium"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-8 mb-12">
      <h1 className="text-3xl font-bold text-center mb-4 text-hooray-red font-mont">
        Reset Your Password
      </h1>
      
      <p className="text-center text-gray-600 mb-6">
        Enter your email address and we'll send you a link to reset your password.
      </p>
      
      {/* Error message display */}
      {error && (
        <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md" 
             role="alert">
          <p className="font-bold">Account Not Found</p>
          <p className="mb-2">{error}</p>
          {error.includes('No account found') && (
            <div className="mt-3 text-sm">
              <p className="font-medium mb-1">Migrating from our old website?</p>
              <p>You'll need to create a new account here first.</p>
              <Link 
                to="/register" 
                className="inline-block mt-2 text-hooray-red hover:text-hooray-yellow font-medium underline"
              >
                Create New Account →
              </Link>
            </div>
          )}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <Formik
          initialValues={{ email: '' }}
          validationSchema={ForgotPasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form>
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <Field
                  type="email"
                  name="email"
                  id="email"
                  placeholder="Enter your email address"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.email && errors.email ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <Button
                type="submit"
                variant="cta"
                disabled={isSubmitting}
                className="w-full mb-4"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
              
              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-hooray-red hover:text-hooray-yellow font-medium"
                >
                  Back to Login
                </Link>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default ForgotPassword; 